import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PushPrompt } from "@/components/PushPrompt";

export const roomLink = (code) => `${window.location.origin}/lobby?game=ludo&room=${code}`;

export const RoomShare = ({ code }) => {
  const { t } = useI18n();
  const copy = async () => {
    try { await navigator.clipboard.writeText(roomLink(code)); toast.success(t("ludo.copied")); } catch { toast.error(roomLink(code)); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4" data-testid="ludo-room-share">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("ludo.roomCode")}</p>
      <p className="font-mono text-3xl font-semibold tracking-[0.3em] text-amber-300" data-testid="ludo-room-code">{code}</p>
      <p className="text-xs text-slate-400">{t("ludo.roomHint")}</p>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${t("ludo.shareText", { code })} ${roomLink(code)}`)}`} target="_blank" rel="noreferrer"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-[#0B0D12]" data-testid="ludo-room-whatsapp-btn">
        <MessageCircle className="h-5 w-5" /> {t("ludo.whatsapp")}
      </a>
      <button type="button" onClick={copy} className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold" data-testid="ludo-room-copy-btn">
        <Copy className="h-4 w-4" /> {t("ludo.copy")}
      </button>
      <PushPrompt />
    </div>
  );
};

export const RoomJoinCard = ({ code, onClose }) => {
  const { t, tErr } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const { data, error } = useQuery({ queryKey: ["ludo-room", code], queryFn: () => api.get(`/ludo/rooms/${code}`).then((r) => r.data), retry: false });
  const join = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/ludo/rooms/${code}/join`);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate(r.status === "matched" ? `/ludo/game/${r.game_id}` : "/ludo/queue");
    } catch (e) { toast.error(tErr(errCode(e))); } finally { setBusy(false); }
  };
  useEffect(() => {
    box.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data, error]);
  if (error) return <div ref={box} className="surface p-5 text-sm text-red-300" data-testid="ludo-room-error">{tErr(errCode(error))} <button className="ml-2 underline" onClick={onClose}>OK</button></div>;
  if (!data) return null;
  const open = data.status === "open";
  return (
    <div ref={box} className="surface space-y-3 border-2 border-amber-500/60 p-5" data-testid="ludo-room-join-card">
      <p className="font-display text-lg font-bold">{t("ludo.roomTitle", { name: data.creator_pseudo })}</p>
      <p className="text-sm text-slate-300" data-testid="ludo-room-info">
        {t("ludo.roomInfo", { n: data.players, mode: data.mode === 2 ? t("ludo.quick") : t("ludo.classic"), stake: formatXAF(data.stake), w: data.waiting })}
      </p>
      {open ? (
        <button type="button" onClick={join} disabled={busy} className="h-12 w-full rounded-full bg-amber-500 font-bold text-[#0B0D12] disabled:opacity-60" data-testid="ludo-room-join-btn">
          {t("ludo.roomJoin", { stake: formatXAF(data.stake) })}
        </button>
      ) : <p className="text-sm text-slate-400" data-testid="ludo-room-closed">{t("ludo.roomClosed")}</p>}
    </div>
  );
};
